import { create, updateField, updateFields, update, remove, getById, getBySingleQuery, getByQuery } from '../utils/dbUtils';
import { isBooleanTrue, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { arrayRemove, arrayUnion, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import Community from './Community';
import AuthUser from './AuthUser';
import User from './User';
import { toLower } from 'lodash';
import { toE164PhoneNumber, toFirstNameLastInitial, toName } from '../utils/stringFormatter';
import Conversation from './Conversation';

class CommunityMember {
    static collectionName = 'communityMembers';

    communityId = null;
    userId = null;

    // Member info (cached from User for quick access)
    email = null;
    phone = null;

    prefix = null;
    firstName = null;
    firstNameLower = null;
    middleName = null;
    lastName = null;
    lastNameLower = null;
    suffix = null;

    imageRefId = null;
    #image = null;

    bio = null;

    displayName = null;
    photoUrl = null;

    // Role flags
    isAdmin = false;
    isModerator = false;

    // Status
    status = 'PENDING'; // PENDING, ACTIVE, BLOCKED, REJECTED, INACTIVE

    metadata = {
        requestedAt: null,
        joinedAt: null,
        leftAt: null,
        lastActiveAt: null,
        blockedAt: null,
        blockedBy: null,
        approvedBy: null
    };

    constructor(member) {
        if (isObjectNotEmpty(member)) {
            if (isStringNotEmpty(member.communityId)) this.communityId = member.communityId;
            if (isStringNotEmpty(member.userId)) this.userId = member.userId;

            // Member info
            if (isStringNotEmpty(member.email)) { this.email = toLower(member.email); }
            if (isStringNotEmpty(member.phone)) { this.phone = toE164PhoneNumber(member.phone); }

            if (isStringNotEmpty(member.firstName)) {
                this.firstName = member.firstName;
                this.firstNameLower = toLower(member.firstName);
            }
            if (isStringNotEmpty(member.lastName)) {
                this.lastName = member.lastName;
                this.lastNameLower = toLower(member.lastName);
            }
            if (isStringNotEmpty(member.prefix)) { this.prefix = member.prefix; }
            if (isStringNotEmpty(member.middleName)) { this.middleName = member.middleName; }
            if (isStringNotEmpty(member.suffix)) { this.suffix = member.suffix; }

            if (isStringNotEmpty(member.imageRefId)) { this.imageRefId = member.imageRefId; }
            if (isNotNullOrUndefined(member.image)) { this.#image = member.image; }

            if (isStringNotEmpty(member.bio)) { this.bio = member.bio; }

            if (isStringNotEmpty(member.displayName)) { this.displayName = member.displayName; }
            if (isStringNotEmpty(member.photoUrl)) { this.photoUrl = member.photoUrl; }

            // Role flags
            this.isAdmin = isBooleanTrue(member.isAdmin);
            this.isModerator = isBooleanTrue(member.isModerator);

            if (isStringNotEmpty(member.status)) { this.status = member.status; }

            if (isObjectNotEmpty(member.metadata)) {
                this.metadata = {
                    ...this.metadata,
                    ...member.metadata
                };
            }
        }
    }

    toDto = () => {
        return {
            communityId: this.communityId,
            userId: this.userId,

            email: this.email,
            phone: this.phone,

            prefix: this.prefix,
            firstName: this.firstName,
            firstNameLower: this.firstNameLower,
            middleName: this.middleName,
            lastName: this.lastName,
            lastNameLower: this.lastNameLower,
            suffix: this.suffix,

            imageRefId: this.imageRefId,

            bio: this.bio,

            displayName: this.displayName,
            photoUrl: this.photoUrl,

            isAdmin: this.isAdmin,
            isModerator: this.isModerator,

            status: this.status,

            metadata: this.metadata
        };
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('UserId is required.');
        }
        if (isStringEmpty(this.communityId)) {
            throw new Error('CommunityId is required.');
        }

        return true;
    }

    // Static methods for managing members
    static create = async (communityId, userId, status) => {
        // Check if user is already a member
        const existingMember = await this.getByCommunityAndUser(communityId, userId);
        if (isObjectNotEmpty(existingMember) && isStringNotEmpty(existingMember?.id)) {
            if (existingMember.status === 'BLOCKED') {
                throw new Error('User is blocked from this community');
            }
            if (existingMember.status === 'REJECTED') {
                throw new Error('User was not approved to join this community');
            }
            if (['ACTIVE', 'PENDING'].includes(existingMember.status)) {
                throw new Error('User is already a member or has a pending request');
            }

            // update existing member
            const updatedMemberObj = new CommunityMember({
                ...existingMember,

                status: status,
                metadata: {
                    requestedAt: new Date().toISOString(),
                    joinedAt: status === 'ACTIVE' ? new Date().toISOString() : null,
                    lastActiveAt: new Date().toISOString(),
                    approvedBy: status === 'ACTIVE' ? userId : null
                }
            });

            // Update the member record
            const updatedMemberSuccess = await update(this.collectionName, existingMember?.id, updatedMemberObj.toDto());
            if (isBooleanTrue(updatedMemberSuccess)) {
                // Update the corresponding array in Community model
                if (status === 'PENDING') {
                    await Community.updateField(communityId, 'pendingMemberIds', arrayUnion(userId));
                } else if (status === 'ACTIVE') {
                    await Community.updateField(communityId, 'memberIds', arrayUnion(userId));
                }

                return { ...updatedMemberObj.toDto() };
            }
        }

        let authUser = await AuthUser.getByUserId(userId);
        let user = await User.getById(userId);

        const newMemberObj = new CommunityMember({
            communityId: communityId,
            userId: userId,

            email: user?.email,
            phone: user?.phone,

            prefix: user?.prefix,
            firstName: user?.firstName,
            firstNameLower: user?.firstNameLower,
            middleName: user?.middleName,
            lastName: user?.lastName,
            lastNameLower: user?.lastNameLower,
            suffix: user?.suffix,

            imageRefId: user?.imageRefId,

            bio: null,

            displayName: toFirstNameLastInitial(user),
            photoUrl: authUser?.photoURL ?? null,

            status: status,
            metadata: {
                requestedAt: new Date().toISOString(),
                joinedAt: status === 'ACTIVE' ? new Date().toISOString() : null,
                lastActiveAt: new Date().toISOString(),
                approvedBy: status === 'ACTIVE' ? userId : null
            }
        });

        // Create the member record
        const newMember = await create(CommunityMember.collectionName, newMemberObj.toDto());

        // Update the corresponding array in Community model
        if (status === 'PENDING') {
            await Community.updateField(communityId, 'pendingMemberIds', arrayUnion(userId));
        } else if (status === 'ACTIVE') {
            await Community.updateField(communityId, 'memberIds', arrayUnion(userId));
        }

        return newMember;
    }

    static updateStatus = async (communityId, userId, newStatus, actionByUserId) => {
        let member = await this.getByCommunityAndUser(communityId, userId);
        if (!member) return false;

        const oldStatus = member.status;
        member.status = newStatus;
        const now = new Date().toISOString();

        // Handle different status transitions
        let updatedMemberFields = { status: newStatus };
        let communityUpdates = {};

        switch (newStatus) {
            case 'ACTIVE':
                if (oldStatus === 'PENDING') {
                    updatedMemberFields.metadata = {
                        ...member.metadata,
                        joinedAt: now,
                        approvedBy: actionByUserId
                    };
                    communityUpdates = {
                        pendingMemberIds: arrayRemove(userId),
                        memberIds: arrayUnion(userId)
                    };
                }
                break;
            case 'INACTIVE':
                // If the user is leaving (status === 'INACTIVE'), remove them from conversations
                // Get all conversations for this community
                const conversations = await Conversation.getByCommunityId(communityId);

                // Remove user from all conversations
                await Promise.all(conversations.map(conversation => {
                    if (conversation.participantIds?.includes(userId)) {
                        return Conversation.removeParticipant(conversation.id, userId);
                    }
                    return Promise.resolve();
                }));

                updatedMemberFields.metadata = {
                    ...member.metadata,
                    lastActiveAt: now,
                    leftAt: now
                };
                communityUpdates = {
                    pendingMemberIds: arrayRemove(userId),
                    memberIds: arrayRemove(userId),
                    adminIds: arrayRemove(userId),
                    moderatorIds: arrayRemove(userId)
                };
                break;
            case 'BLOCKED':
                updatedMemberFields.metadata = {
                    ...member.metadata,
                    blockedAt: now,
                    blockedBy: actionByUserId
                };
                communityUpdates = {
                    memberIds: arrayRemove(userId),
                    pendingMemberIds: arrayRemove(userId)
                };
                break;
            default:
                break;
        }

        // Update Community model if needed
        if (Object.keys(communityUpdates).length > 0) {
            await Community.updateFields(communityId, communityUpdates);
        }

        return await updateFields(CommunityMember.collectionName, member.id, updatedMemberFields);
    }

    static updateRole = async (communityId, userId, role, value = false) => {
        const member = await this.getByCommunityAndUser(communityId, userId);
        if (!member) return false;

        const updateMemberFields = {};
        const communityUpdates = {};

        if (role === 'ADMIN') {
            updateMemberFields.isAdmin = value;
            if (value) {
                communityUpdates.adminIds = arrayUnion(userId);
            } else {
                communityUpdates.adminIds = arrayRemove(userId);
            }
        } else if (role === 'MODERATOR') {
            updateMemberFields.isModerator = value;
            if (value) {
                communityUpdates.moderatorIds = arrayUnion(userId);
            } else {
                communityUpdates.moderatorIds = arrayRemove(userId);
            }
        }

        // Update both CommunityMember and Community
        await Promise.all([
            updateFields(CommunityMember.collectionName, member.id, updateMemberFields),
            Community.updateFields(communityId, communityUpdates)
        ]);

        return true;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(this.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(this.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedEntity, oldObj) => {
        const updatedObj = new CommunityMember(updatedEntity);
        if (updatedObj.isValid() && id === updatedEntity.id) {
            const success = await update(this.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated member must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(this.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(this.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static checkMembershipStatus = async (communityId, userId) => {
        const member = await this.getByCommunityAndUser(communityId, userId);
        if (!member) return { isMember: false, status: null };
        return {
            isMember: member.status === 'ACTIVE',
            status: member.status,
            isBlocked: member.status === 'BLOCKED',
            isPending: member.status === 'PENDING',
            metadata: member.metadata
        };
    }

    static getByUserId = async (userId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('userId', '==', userId),
            where('status', '==', 'ACTIVE')
        );
        return await getByQuery(q);
    }

    static getByUserIdAndRole = async (userId, role) => {
        const roleField = role === 'ADMIN' ? 'isAdmin' : 'isModerator';
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('userId', '==', userId),
            where(roleField, '==', true),
            where('status', '==', 'ACTIVE')
        );
        return await getByQuery(q);
    }

    static getByCommunityId = async (communityId) => {
        return await getBySingleQuery(CommunityMember.collectionName, 'communityId', communityId, '==');
    }

    static getByCommunityAndUser = async (communityId, userId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('communityId', '==', communityId),
            where('userId', '==', userId)
        );
        const members = await getByQuery(q);
        return members?.[0] || null;
    }

    static getActiveMembersByCommunity = async (communityId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('communityId', '==', communityId),
            where('status', '==', 'ACTIVE')
        );
        return await getByQuery(q);
    }

    static getPendingMembersByCommunity = async (communityId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('communityId', '==', communityId),
            where('status', '==', 'PENDING')
        );
        return await getByQuery(q);
    }

    static getAdminsByCommunity = async (communityId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('communityId', '==', communityId),
            where('isModerator', '==', true)
        );
        return await getByQuery(q);
    }

    static getAdminsByCommunity = async (communityId) => {
        const q = query(
            collection(db, CommunityMember.collectionName),
            where('communityId', '==', communityId),
            where('isAdmin', '==', true)
        );
        return await getByQuery(q);
    }
}

export default CommunityMember;

// // Check membership before adding
// const { status, isBlocked } = await CommunityMember.checkMembershipStatus(communityId, userId);
// if (isBlocked) {
//     // Handle blocked user
// }

// // Approve a member
// await CommunityMember.updateStatus(communityId, userId, 'ACTIVE', adminUserId);

// // Block a member
// await CommunityMember.updateStatus(communityId, userId, 'BLOCKED', moderatorUserId);

// // Update role
// await CommunityMember.updateRole(communityId, userId, 'MODERATOR', true);

// // join:
// await CommunityMember.create(communityId, userId, 'ACTIVE');

// // request to join
// await CommunityMember.create(communityId, userId, 'PENDING');

// // approveMember:
// await CommunityMember.updateStatus(communityId, userId, 'ACTIVE', currentUserId);

// // rejectMember:
// await CommunityMember.updateStatus(communityId, userId, 'REJECTED', currentUserId);

// // leave:
// await CommunityMember.updateStatus(communityId, userId, 'INACTIVE');